import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, Loader2Icon, SchoolIcon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
// Validation Schema
const createClassroomSchema = z.object({
  name: z.string().min(1, 'Classroom name is required'),
  grade: z.string().min(1, 'Grade level is required'),
  section: z.string().optional(),
  academicYearId: z.string().min(1, 'Academic year is required')
});
type CreateClassroomFormValues = z.infer<typeof createClassroomSchema>;
interface CreateClassroomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateClassroomFormValues) => Promise<void>;
  academicYears: {
    id: string;
    name: string;
  }[];
  defaultYearId: string;
}
export const CreateClassroomModal: React.FC<CreateClassroomModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  academicYears,
  defaultYearId
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset
  } = useForm<CreateClassroomFormValues>({
    resolver: zodResolver(createClassroomSchema),
    defaultValues: {
      academicYearId: defaultYearId
    }
  });
  const onSubmit = async (data: CreateClassroomFormValues) => {
    try {
      await onCreate(data);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to create classroom:', error);
    }
  };
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-1092">
      <AnimatePresence data-id="element-1093">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-1094">
            <Dialog.Overlay asChild data-id="element-1095">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-1096">
                <Dialog.Content asChild data-id="element-1097">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-1098">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0" data-id="element-1099">
                      <div className="flex items-center gap-3" data-id="element-1100">
                        <div className="w-10 h-10 rounded-xl bg-[#00B75F]/10 flex items-center justify-center text-[#00B75F]" data-id="element-1101">
                          <SchoolIcon size={20} data-id="element-1102" />
                        </div>
                        <div data-id="element-1103">
                          <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-1104">
                            Create Classroom
                          </Dialog.Title>
                          <Dialog.Description className="text-xs text-text-secondary" data-id="element-1105">
                            Set up a new cohort for this academic year
                          </Dialog.Description>
                        </div>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1106">
                        <XIcon size={20} data-id="element-1107" />
                      </button>
                    </div>

                    {/* Form - Scrollable */}
                    <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" data-id="element-1108">
                      <div className="p-6 space-y-5" data-id="element-1109">
                        {/* Name Field */}
                        <div className="space-y-1.5" data-id="element-1110">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1111">
                            Classroom Name{' '}
                            <span className="text-red-500" data-id="element-1112">*</span>
                          </label>
                          <input {...register('name')} placeholder="e.g. Grade 7A Science" className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.name ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1113" />
                          {errors.name && <p className="text-xs text-red-500 font-medium" data-id="element-1114">
                              {errors.name.message}
                            </p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-1115">
                          {/* Grade Field */}
                          <div className="space-y-1.5" data-id="element-1116">
                            <label className="text-sm font-semibold text-text-primary" data-id="element-1117">
                              Grade Level{' '}
                              <span className="text-red-500" data-id="element-1118">*</span>
                            </label>
                            <select {...register('grade')} className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.grade ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1119">
                              <option value="" data-id="element-1120">Select Grade</option>
                              {[...Array(12)].map((_, i) => <option key={i} value={`Grade ${i + 1}`} data-id="element-1121">
                                  Grade {i + 1}
                                </option>)}
                              <option value="Kindergarten" data-id="element-1122">Kindergarten</option>
                            </select>
                            {errors.grade && <p className="text-xs text-red-500 font-medium" data-id="element-1123">
                                {errors.grade.message}
                              </p>}
                          </div>

                          {/* Section Field */}
                          <div className="space-y-1.5" data-id="element-1124">
                            <label className="text-sm font-semibold text-text-primary" data-id="element-1125">
                              Section{' '}
                              <span className="text-text-tertiary font-normal" data-id="element-1126">
                                (Optional)
                              </span>
                            </label>
                            <input {...register('section')} placeholder="e.g. A, B, 101" className="w-full px-3 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F] transition-all" data-id="element-1127" />
                          </div>
                        </div>

                        {/* Academic Year Field */}
                        <div className="space-y-1.5" data-id="element-1128">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1129">
                            Academic Year
                          </label>
                          <div className="relative" data-id="element-1130">
                            <select {...register('academicYearId')} disabled className="w-full px-3 py-2.5 bg-gray-50 border border-custom rounded-xl text-sm text-text-secondary focus:outline-none cursor-not-allowed" data-id="element-1131">
                              {academicYears.map(year => <option key={year.id} value={year.id} data-id="element-1132">
                                  {year.name}
                                </option>)}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#00B75F] font-medium pointer-events-none" data-id="element-1133">
                              Active Year
                            </div>
                          </div>
                          <p className="text-[10px] text-text-tertiary" data-id="element-1134">
                            Classrooms are always created in the active academic
                            year.
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="pt-4 flex items-center gap-3" data-id="element-1135">
                          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-1136">
                            Cancel
                          </button>
                          <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1137">
                            {isSubmitting ? <>
                                <Loader2Icon size={16} className="animate-spin" data-id="element-1138" />
                                Creating...
                              </> : 'Create Classroom'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};